import { FinishType } from '@cc-shopping/platform-analytics/types';
import { datadogRum } from '@datadog/browser-rum';
import {
  CHASE_LANDING_PAGES,
  FORWARDED_PARAMS,
  type ForwardedParam,
  PARTNER_PARAM_DEFAULTS,
} from './constants';

/** Supplied by `CtaLink`, not by the sku-viewed payload, so never report them as missing. */
const PARAMS_FROM_CTA_LINK = new Set<ForwardedParam>([
  'calling_deployable_name',
  'calling_deployable_version',
  'cta_type',
]);

export type ChaseLandingPageContext = {
  offerId: string;
  isMonetizing: boolean;
  pathname: string;
  ctaType?: string;
  skuViewedPayload?: Partial<Record<ForwardedParam, string | number | boolean | undefined>>;
};

export function getLandingPageAltLink(offerId: string, pathname: string) {
  const landingPage = CHASE_LANDING_PAGES[offerId];
  return landingPage?.pathnames.includes(pathname) ? landingPage.altLinkName : undefined;
}

export function isChaseLandingPageLink(applyLink: string): boolean {
  const altLinkNames = Object.values(CHASE_LANDING_PAGES).map(page => page.altLinkName);
  try {
    const alt = new URL(applyLink).searchParams.get('alt');
    return !!alt && altLinkNames.includes(alt);
  } catch {
    return false;
  }
}

export function getFinishTypeForApplyLink(applyLink: string, finishType?: FinishType) {
  return isChaseLandingPageLink(applyLink) ? FinishType.InternalContent : finishType;
}

export function copyForwardedParamsToApplyLink(applyLink: string, pageUrl: string): string {
  let link: URL;
  let page: URL;
  try {
    link = new URL(applyLink);
    page = new URL(pageUrl);
  } catch {
    datadogRum.addError(new Error('Invalid Chase landing page apply link'), {
      applyLink,
      pageUrl,
    });
    return applyLink;
  }

  for (const forwardedParam of FORWARDED_PARAMS) {
    const value = page.searchParams.get(forwardedParam);
    if (value) {
      link.searchParams.set(forwardedParam, value);
    }
  }

  return link.toString();
}

export function listForwardedParamsMissingFromPageUrl(pageUrl: string): ForwardedParam[] {
  let page: URL;
  try {
    page = new URL(pageUrl);
  } catch {
    return [];
  }

  return FORWARDED_PARAMS.filter(key => {
    const comesFromCtaLink = PARAMS_FROM_CTA_LINK.has(key);
    const hasValue = (page.searchParams.get(key) ?? '') !== '';
    const hasDefault = PARTNER_PARAM_DEFAULTS[key] !== undefined;

    return !comesFromCtaLink && !hasValue && hasDefault;
  });
}

export function listParamsThatFellBackToDefaults(
  skuViewedPayload: ChaseLandingPageContext['skuViewedPayload'],
): ForwardedParam[] {
  return FORWARDED_PARAMS.filter(key => {
    const comesFromCtaLink = PARAMS_FROM_CTA_LINK.has(key);
    const hasValue = String(skuViewedPayload?.[key] ?? '') !== '';
    const hasDefault = PARTNER_PARAM_DEFAULTS[key] !== undefined;

    return !comesFromCtaLink && !hasValue && hasDefault;
  });
}

function getImpressionPageNameForRedirect(
  skuViewedPayload: ChaseLandingPageContext['skuViewedPayload'],
): string | undefined {
  const fromPayload = String(skuViewedPayload?.impression_page_name ?? '');
  return fromPayload || PARTNER_PARAM_DEFAULTS.impression_page_name;
}

function buildPartnerParams(context: ChaseLandingPageContext): URLSearchParams {
  const partnerParams = new URLSearchParams();

  for (const key of FORWARDED_PARAMS) {
    const value = String(context.skuViewedPayload?.[key] ?? '');
    const resolved = value || PARTNER_PARAM_DEFAULTS[key];

    if (resolved) {
      partnerParams.set(key, resolved);
    }
  }

  // The one field CtaLink knows and the payload does not
  if (context.ctaType) {
    partnerParams.set('cta_type', context.ctaType);
  }
  partnerParams.set('finish_type', FinishType.ExternalApplication);

  return partnerParams;
}

export function applyChaseLandingPage(href: string, context: ChaseLandingPageContext): string {
  const altLinkName = getLandingPageAltLink(context.offerId, context.pathname);
  if (!altLinkName || !context.isMonetizing || !href) {
    return href;
  }

  let applyLink: URL;
  try {
    applyLink = new URL(href);
  } catch {
    return href;
  }

  applyLink.searchParams.set('alt', altLinkName);
  applyLink.searchParams.set('supressSplashPage', 'true');

  const impressionPageName = getImpressionPageNameForRedirect(context.skuViewedPayload);
  if (impressionPageName) {
    applyLink.searchParams.set('impression_page_name', impressionPageName);
  }

  applyLink.searchParams.set('partnerParams', buildPartnerParams(context).toString());

  return applyLink.toString();
}
